import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import * as Styled from "./styles";

import "filepond/dist/filepond.min.css";
import { FilePond, registerPlugin } from "react-filepond";

import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import FilePondPluginImageExifOrientation from "filepond-plugin-image-exif-orientation";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";

import localeActions from "Redux/Locale/actions";
import { ReducerState } from "Redux/store";

import ButtonPrimary from "Components/Buttons/Primary";
import Form from "Components/Form";
import TextInput from "Components/Form/Inputs/Text";
import validators from "Components/Form/Inputs/Text/Validations";
import api from "Helpers/Api";
import BackendErrorHelper from "Helpers/BackendErrorHelper";
import { convertPhone } from "Helpers/stringPhone";

import IconUserResearchNotCss from "Assets/Svg/user-research-not-css.jsx";
import CheckBox from "Components/Form/Inputs/CheckBox";
import defaultAxios from "axios";

import RadioSet from "Components/Form/Inputs/RadioSet";
import HoverInfoIcon from "Components/Info";
import LoadingIcon from "Components/LoadingIcon";
import { checkCpfCnpj } from "Helpers/checkCpfCnpj";
import { PERSON_TYPES } from "Helpers/consts";
import UserExistsModal from "Components/Modal/UserExistsModal";
import Routes from "Routes";
import sleep from "Helpers/sleep";
import UserDontExistsModal from "Components/Modal/UserDontExistsModal";

interface Result {
  status: boolean;
  message: string;
}

interface Props {
  formDisabled?: boolean;
  formTitle?: string;
  formButtonText?: string;
  formRedirectLink?: string | null;
  crucialLeadCapture?: boolean;
  handleModal?: React.Dispatch<React.SetStateAction<boolean>>;
}

interface ILocation {
  name: string;
  value: number;
}

interface IUserStorage {
  name: string;
  email: string;
  phone: string;
  tries?: number;
}

interface IUserDocuments {
  cpf?: string;
  cnpj?: string;
}

// Register the plugins
registerPlugin(FilePondPluginImageExifOrientation);
registerPlugin(FilePondPluginFileValidateSize);

const FormStores: React.FC<Props> = ({
  formDisabled = false,
  formButtonText = "Enviar",
  formRedirectLink,
  crucialLeadCapture = false,
  handleModal,
}) => {
  const token: string = useSelector((state: ReducerState) => state.auth.token);
  const user = useSelector((state: ReducerState) => state.auth.user);
  const history = useHistory();
  const location = useLocation();

  api.defaults.headers["Authorization"] = `bearer ${token}`;

  const dispatch = useDispatch();

  const [name, setName] = useState<string | null>("");
  const [storeName, setStoreName] = useState<string | null>("");
  const [email, setEmail] = useState<string | null>((user && user.email) || "");
  const [phone, setPhone] = useState<string | null>((user && user.phone) || "");
  const [isWhatsApp, setIsWhatsApp] = useState(false);
  // Not find types to use with filepond, if has any update on lib i will change this any type
  const [bannerFile, setBannerFile] = useState<any>([]);
  const [logoFile, setLogoFile] = useState<any>([]);
  const [website, setWebsite] = useState("");
  const [description, setDescription] = useState("");
  const [result, setResult] = useState<Result | null>(null);
  const [sent, setSent] = useState("");
  const [isSubmmiting, setIsSubmitting] = useState(false);
  const [useUser, setUseUser] = useState(false);
  const [radio, setRadio] = useState<string>(PERSON_TYPES.NATURAL);
  const userDocuments = (user || {}) as IUserDocuments;
  const [userCpf, setUserCpf] = useState<string>(userDocuments.cpf || "");
  const [userCnpj, setUserCnpj] = useState<string>(userDocuments.cnpj || "");
  const [defaultError, setDefaultError] = useState<string[]>();

  const FORM_STORAGE_KEY = "partnerFormData";

  // Modal state for existing user
  const [showUserExistsModal, setShowUserExistsModal] = useState(false);
  const [existingUserInfo, setExistingUserInfo] = useState<{
    field: string;
    message: string;
  } | null>(null);

  // Modal state for inexisting user
  const [showUserDontExistsModal, setShowUserDontExistsModal] = useState(false);

  // verify if email or cpf/cnpj already exists in the system
  const verify = async (field: string, value: string) => {
    try {
      if (!value) {
        return `${field} é obrigatório.`;
      }
      const response = await api.get(`users/verify/${field}`, {
        params: { check: value },

        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      return !response.data.status;
    } catch (error) {
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Load form data from localStorage on mount if returning from login
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromLogin = params.get("fromLogin");

    if (fromLogin === "true" && user) {
      const savedData = localStorage.getItem(FORM_STORAGE_KEY);
      if (savedData) {
        try {
          const formData = JSON.parse(savedData);
          setStoreName(formData.storeName || "");
          setName(formData.name || "");
          setRadio(formData.radio || PERSON_TYPES.NATURAL);
          setPhone(formData.phone || "");
          setIsWhatsApp(formData.isWhatsApp || false);
          setWebsite(formData.website || "");
          setDescription(formData.description || "");
          // Don't restore email, phone, cpf, cnpj as they belong to the logged user
          setUseUser(true);

          // Clear storage after restoring
          localStorage.removeItem(FORM_STORAGE_KEY);

          dispatch(
            localeActions.addTooltip({
              type: "success",
              message:
                "Bem-vindo de volta! Continue o cadastro da sua loja. Por favor, adicione novamente o banner e o logo.",
            })
          );
        } catch (error) {
          console.error("Error loading form data:", error);
        }
      }
    }
  }, [location, user, dispatch]);

  type TInput = {
    storeName: string;
    name: string;
    email: string;
    cpf: string;
    cnpj: string;
    radio: string;
    phone: string;
    isWhatsApp: boolean;
    website: string;
    description: string;
  };

  function showErrors(message: string) {
    setResult({
      status: false,
      message,
    });
    dispatch(
      localeActions.addTooltip({
        type: "error",
        message,
      })
    );
  }

  // Save form data to localStorage
  const saveFormDataToStorage = (data: any) => {
    try {
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error("Error saving form data:", error);
    }
  };

  // Handle redirect to login
  const handleLoginRedirect = () => {
    // Save current form data
    const formData = {
      storeName,
      name,
      radio,
      phone,
      isWhatsApp,
      website,
      description,
      timestamp: new Date().getTime(),
    };
    saveFormDataToStorage(formData);

    // Redirect to login with return URL
    history.push(`${Routes.login}?returnTo=oficialstores`);
  };

  let verifyExistingEmail = false;
  let verifyexistingCpfCnpj = false;

  //handle redirect to user registration
  const handleRegistrationRedirect = () => {
    history.push(`${Routes.registration}?returnTo=oficialstores`);
  };

  // Handle modal cancel - user wants to try different email/cpf
  const handleModalCancel = () => {
    setShowUserExistsModal(false);
    setExistingUserInfo(null);
    setShowUserDontExistsModal(false);
    verifyExistingEmail = false;
    verifyexistingCpfCnpj = false;

    dispatch(
      localeActions.addTooltip({
        type: "warning",
        message: "Por favor, tente com outro email ou CPF/CNPJ.",
      })
    );
  };

  async function send({
    storeName,
    name,
    email,
    cnpj,
    cpf,
    radio,
    phone,
    isWhatsApp,
    website,
    description,
  }: TInput) {
    setIsSubmitting(true);

    const normalizedEmail = email || "";
    const normalizedCpf = (cpf || "").replace(/\D/g, "").toString();
    const normalizedCnpj = (cnpj || "").replace(/\D/g, "").toString();

    if (!user) {
      verifyExistingEmail = Boolean(await verify("email", normalizedEmail));
      verifyexistingCpfCnpj = Boolean(
        cpf
          ? await verify("cpf", normalizedCpf)
          : await verify("cnpj", normalizedCnpj)
      );
    }

    if (!user && (verifyExistingEmail || verifyexistingCpfCnpj)) {
      setShowUserExistsModal(true);
      setExistingUserInfo({
        field: verifyExistingEmail ? "email" : cpf ? "cpf" : "cnpj",
        message: verifyExistingEmail
          ? "Este email já está em uso."
          : cpf
          ? "Este CPF já está em uso."
          : "Este CNPJ já está em uso.",
      });
      return;
    } else if (!user && !verifyExistingEmail && !verifyexistingCpfCnpj) {
      dispatch(
        localeActions.addTooltip({
          type: "warning",
          message:
            "Crie sua conta para continuar. Em seguida você retornará para finalizar o cadastro da loja.",
        })
      );
      // Note: File objects (bannerFile, logoFile) cannot be serialized to JSON
      // and will need to be re-uploaded after registration/login.
      const formData = {
        storeName,
        name,
        cpf,
        cnpj,
        radio,
        email,
        phone,
        isWhatsApp,
        website,
        description,
        timestamp: new Date().getTime(),
      };
      saveFormDataToStorage(formData);
      setShowUserDontExistsModal(true);

      setIsSubmitting(false);
      return;
    }

    if (useUser && !userCpf && !userCnpj) {
      showErrors(
        "Seu usuário logado não possui CPF/CNPJ cadastrado. Desmarque o vínculo ou atualize seu cadastro."
      );
      setIsSubmitting(false);
      return;
    }

    if (!bannerFile.length) {
      showErrors("Insira um banner pra ser apresentado na sua loja.");
      setIsSubmitting(false);
      return;
    }

    const formdata = new FormData();

    const validCnpjCpf = useUser
      ? checkCpfCnpj({ cnpj: userCnpj, cpf: userCpf })
      : checkCpfCnpj({ cnpj, cpf });

    if (validCnpjCpf?.cnpj) {
      formdata.append("cnpj", validCnpjCpf.cnpj);
    }

    if (validCnpjCpf?.cpf) {
      formdata.append("cpf", validCnpjCpf.cpf);
    }

    user && formdata.append("userId", user.id);
    name && formdata.append("name", name);
    storeName && formdata.append("storeName", storeName);
    email && formdata.append("email", email);
    description && formdata.append("description", description);
    phone && formdata.append("phone", convertPhone(phone));
    website && formdata.append("website", website.toLocaleLowerCase());
    // TODO: See why not accept boolean as value here, change on backend.
    isWhatsApp && formdata.append("whatsapp", "true");
    bannerFile.length > 0 && formdata.append("banner", bannerFile[0].file);
    logoFile.length > 0 && formdata.append("logo", logoFile[0].file);

    const tooltipMessage = crucialLeadCapture
      ? "Boa sorte!"
      : "Contato enviado com sucesso";

    try {
      const response = await api.post(`/partners/create`, formdata, {
        headers: {
          Accept: "application/json",
          "Content-Type": "multipart/form-data",
        },
      });

      setResult({ status: true, message: tooltipMessage });

      setSent(response.data.message);
    } catch (e) {
      if (defaultAxios.isAxiosError(e)) {
        // Check if it's a user already exists error (409 Conflict)
        if (
          e.response?.status === 409 &&
          e.response?.data?.error?.type === "user_already_exists"
        ) {
          const errorData = e.response.data.error;
          setExistingUserInfo({
            field: errorData.field,
            message: errorData.message,
          });
          setShowUserExistsModal(true);
          setIsSubmitting(false);
          return;
        }

        if (BackendErrorHelper.checkIfDefaultError(e)) {
          const message = BackendErrorHelper.getDefaultErrorMessage(e.response);
          showErrors(message);
        } else if (BackendErrorHelper.checkIfValidationError(e)) {
          const message = BackendErrorHelper.getValidatorErrorMessage(
            e.response
          );
          showErrors(message);
        } else {
          showErrors("Erro na requisição, por favor tente novamente");
        }
      } else {
        showErrors("Verifique se todos os campos estão completos.");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const getUserCpfCnpj = async () => {
    if (!user?.id) return null;

    const res = await api.get(`/users/${user.id}`);
    const data = res.data || {};

    setUserCpf(data.cpf || "");
    setUserCnpj(data.cnpj || "");
    setEmail(data.email || "");

    if (data.cpf) {
      setRadio(PERSON_TYPES.NATURAL);
    } else if (data.cnpj) {
      setRadio(PERSON_TYPES.JURIDICAL);
    }

    return data;
  };

  useEffect(() => {
    if (!user?.id) return;

    const loadUserDocuments = async () => {
      try {
        await getUserCpfCnpj();
      } catch (error) {
        console.error("Erro ao buscar CPF/CNPJ do usuário:", error);
      }
    };

    loadUserDocuments();
  }, [user?.id]);

  useEffect(() => {
    if (!useUser) return;

    setEmail(user?.email || "");
    setUserCpf(userDocuments.cpf || "");
    setUserCnpj(userDocuments.cnpj || "");
  }, [useUser]);

  return (
    <Styled.Container disable={formDisabled}>
      {showUserExistsModal && existingUserInfo && (
        <UserExistsModal
          field={existingUserInfo.field}
          message={existingUserInfo.message}
          onLogin={handleLoginRedirect}
          onCancel={handleModalCancel}
        />
      )}
      {showUserDontExistsModal &&
        (!existingUserInfo || existingUserInfo === null) && (
          <UserDontExistsModal
            onLogin={handleRegistrationRedirect}
            onCancel={handleModalCancel}
          />
        )}
      {sent ? (
        <>
          <Styled.SentMessageContainer>
            <Styled.SentMessageText>{sent}</Styled.SentMessageText>
            <Styled.ImageContainer>
              <IconUserResearchNotCss />
            </Styled.ImageContainer>
          </Styled.SentMessageContainer>
        </>
      ) : (
        <>
          <Styled.Title>Desejo fazer parte.</Styled.Title>
          <Styled.Result status={result ? result.status : true}>
            {result && result.message}
          </Styled.Result>
          <Form submit={send} defaultError={defaultError}>
            <>
              <Styled.FormRow>
                <TextInput
                  name="storeName"
                  label="Nome da loja*"
                  change={setStoreName}
                  placeholder="Digite o nome da loja aqui"
                  defaultValue={storeName || ""}
                  required
                />
              </Styled.FormRow>
              <Styled.FormRow>
                <TextInput
                  name="name"
                  label="Responsável*"
                  change={setName}
                  placeholder="Digite o nome do responsável"
                  defaultValue={name || ""}
                  required
                />
              </Styled.FormRow>
              <Styled.FormColumn>
                <TextInput
                  name="email"
                  label="Email*"
                  placeholder={useUser ? email! : "Digite seu email"}
                  required
                  defaultValue={useUser ? email! : ""}
                  validation="email"
                  disabled={useUser}
                  {...(useUser && { lockValue: email! })}
                />
                {user && (
                  <div className="user-container">
                    <CheckBox
                      name="user"
                      checked={useUser}
                      change={isChecked => setUseUser(isChecked)}
                    >
                      <Styled.InfoMessage>
                        <Styled.Terms>
                          Desejo vincular essa loja ao meu usuário
                        </Styled.Terms>
                        <HoverInfoIcon message="Vincularemos a loja atual ao seu email logado na plataforma." />
                      </Styled.InfoMessage>
                    </CheckBox>
                  </div>
                )}
              </Styled.FormColumn>
              <Styled.FormColumn>
                <RadioSet
                  name="radioCpfCnpj"
                  defaultValue={radio}
                  options={Object.values(PERSON_TYPES).map(type => ({
                    name: type,
                    value: type,
                  }))}
                  change={value => {
                    setRadio(value);
                  }}
                  disabled={useUser}
                />
                {radio === PERSON_TYPES.NATURAL ? (
                  <TextInput
                    name="cpf"
                    placeholder={
                      useUser && userCpf
                        ? userCpf.slice(0, 3) +
                          userCpf.slice(3, 6) +
                          userCpf.slice(6, 9) +
                          userCpf.slice(9)
                        : "000.000.000-00"
                    }
                    required
                    validation="cpfcnpj"
                    mask={"999.999.999-99"}
                    defaultValue={useUser ? userCpf : ""}
                    disabled={useUser}
                  />
                ) : (
                  <TextInput
                    name="cnpj"
                    placeholder={useUser ? userCnpj : "00.000.000/0000-00"}
                    required
                    validation="cpfcnpj"
                    mask={"99.999.999/9999-99"}
                    defaultValue={useUser ? userCnpj : ""}
                    disabled={useUser}
                  />
                )}
              </Styled.FormColumn>
              <Styled.FormColumn>
                <TextInput
                  name="phone"
                  label="Telefone*"
                  change={setPhone}
                  required
                  placeholder="Digite seu telefone"
                  defaultValue={phone || ""}
                  mask={"(99) 99999-9999"}
                  validation="phone"
                />
                <div className="whatsapp-container">
                  <CheckBox
                    name="whatsapp"
                    checked={isWhatsApp}
                    change={isChecked => setIsWhatsApp(isChecked)}
                  >
                    <Styled.Terms>Este número é WhatsApp.</Styled.Terms>
                  </CheckBox>
                </div>
              </Styled.FormColumn>
              <Styled.FormRow>
                <TextInput
                  name="website"
                  label="URL da sua loja*"
                  change={setWebsite}
                  placeholder="Digite aqui o URL da sua loja"
                  defaultValue={website}
                  required
                  validation="url"
                />
              </Styled.FormRow>
              <Styled.FormRow>
                <FilePond
                  maxFileSize="5MB"
                  files={bannerFile}
                  allowMultiple={false}
                  onupdatefiles={setBannerFile}
                  storeAsFile={true}
                  labelIdle={`
                  <div class="content-filepond">
                      <svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 16 16" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M.002 3a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-12a2 2 0 0 1-2-2V3zm1 9v1a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062L1.002 12zm5-6.5a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0z"></path></svg>
                      <p>Clique ou arraste e solte seu banner aqui.</p>
                  </div>`}
                  credits={false}
                />
              </Styled.FormRow>
              <Styled.FormRow>
                <FilePond
                  maxFileSize="5MB"
                  files={logoFile}
                  allowMultiple={false}
                  onupdatefiles={setLogoFile}
                  storeAsFile={true}
                  labelIdle={`
                  <div class="content-filepond">
                      <svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 16 16" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M.002 3a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-12a2 2 0 0 1-2-2V3zm1 9v1a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062L1.002 12zm5-6.5a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0z"></path></svg>
                      <p>Clique ou arraste e solte sua Logo aqui.</p>
                  </div>`}
                  credits={false}
                />
              </Styled.FormRow>
              <Styled.FormRow>
                <TextInput
                  name="description"
                  label="Descrição da sua loja*"
                  defaultValue={description}
                  required
                  textarea
                  placeholder="Essa descrição irá aparecer na sua loja"
                />
              </Styled.FormRow>
              <Styled.FormRow>
                <CheckBox name="terms" required>
                  <Styled.Terms>
                    Autorizo a utilização dos meus dados de acordo com as{" "}
                    <a href="/informacoes/privacidade-confidencialidade">
                      Políticas de Privacidade
                    </a>{" "}
                    e aceito os{" "}
                    <a href="/informacoes/termo-lojas-online">
                      Termos e Condições
                    </a>
                  </Styled.Terms>
                </CheckBox>
              </Styled.FormRow>
              <Styled.SubmitContainer>
                <Styled.ButtonsContainer>
                  <ButtonPrimary type="submit" color="orange">
                    {isSubmmiting ? <LoadingIcon /> : <>{formButtonText}</>}
                  </ButtonPrimary>
                </Styled.ButtonsContainer>
              </Styled.SubmitContainer>
            </>
          </Form>
        </>
      )}
    </Styled.Container>
  );
};

export default FormStores;
